import React, { useCallback, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import isEmpty from 'lodash/isEmpty';

import Badge from 'components/Badge/Badge';
import Input from 'components/Input/Input';
import RichText from 'components/RichText/RichText';
import Typography from 'components/Typography/Typography';
import { Separator } from 'components/ui/separator';

interface ISegmentSelectorProps {
  segments: string[];
  onSegmentsChange: (segments: string[]) => void;
  inputTestId: string;
  listTestId?: string;
  placeholder?: string;
  title?: string;
  description?: string;
}

/**
 * Shared segment selector with add-on-enter and badge removal.
 *
 * @example
 * <SegmentSelector
 *   segments={chosenSegment}
 *   onSegmentsChange={(newSegments) => {
 *     setChosenSegment(newSegments);
 *     setFieldValue('segments', newSegments);
 *   }}
 *   inputTestId="promoSegmentsInpt"
 * />
 */
export const SegmentSelector: React.FC<ISegmentSelectorProps> = ({
  segments,
  onSegmentsChange,
  inputTestId,
  listTestId,
  placeholder = 'Search Segment',
  title = 'Segments',
  description = 'Create or add players segments to your offer'
}): React.ReactElement => {
  const [inputValue, setInputValue] = useState('');
  const [segmentError, setSegmentError] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleAdd = useCallback(
    (value: string): void => {
      if (segments.includes(value)) {
        setSegmentError("You can't add the same segment twice");
        return;
      }

      const newSegments = [...segments, value];
      onSegmentsChange(newSegments);
      setSegmentError('');
      setInputValue('');
      inputRef.current?.blur();
    },
    [segments, onSegmentsChange]
  );

  const handleRemove = useCallback(
    (value: string): void => {
      const newSegments = segments.filter((s) => s !== value);
      onSegmentsChange(newSegments);
    },
    [segments, onSegmentsChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter' && inputValue.trim()) {
        e.preventDefault();
        handleAdd(inputValue.trim());
      }
    },
    [inputValue, handleAdd]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setInputValue(e.target.value);
      setSegmentError(
        segments.includes(e.target.value)
          ? "You can't add the same segment twice"
          : ''
      );
    },
    [segments]
  );

  return (
    <>
      <Separator className="my-5 bg-base-border" />
      <RichText
        title={title}
        description={description}
        className="mt-4 mb-5"
      />
      <div className="flex flex-col gap-5">
        <div>
          <Typography className="text-black font-medium text-sm mb-2">
            Segment Name
          </Typography>
          <Input
            ref={inputRef}
            value={inputValue}
            placeholder={placeholder}
            dataTestId={inputTestId}
            rightIcon={
              <FontAwesomeIcon
                icon="fa-regular fa-magnifying-glass"
                className="w-4 h-4"
              />
            }
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            state={segmentError ? 'error' : 'default'}
            description={segmentError || ''}
          />
          {isEmpty(segments) && (
            <Typography className="text-zinc-500 text-sm mt-2">
              Press 'Enter' to add a new segment
            </Typography>
          )}
          {!isEmpty(segments) && (
            <div
              className="mt-3 flex flex-row flex-wrap gap-2"
              data-testid={listTestId}
            >
              {segments.map((val: string) => (
                <Badge
                  key={val}
                  isRemovable
                  onRemove={() => handleRemove(val)}
                >
                  {val}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
      <Separator className="my-5 bg-base-border" />
    </>
  );
};
